import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  faDownload,
  faEnvelope,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { CustomStep, ModalComponent } from 'marketpro-design-lib';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { constants } from '../../../../constants';
import { AuthService } from '../../../auth/services/auth.service';
import { AuthorizedResourcesService } from '../../../auth/services/authorized-resources.service';
import { AgeGroup, allAgeGroupsLabel } from '../../models/age-group.model';
import {
  NameValuePair,
  ToggleableNameValuePair,
} from '../../models/name-value-pair.model';
import { RangeField } from '../../models/range-field.model';
import { RegionSelection } from '../../models/region-selection.model';
import {
  allSpendingGroupsLabel,
  SpendingGroup,
} from '../../models/spending-group.model';
import { DashboardStatisticsService } from '../../services/dashboard-statistics.service';
import {
  DataFilters,
  DataFiltersService,
} from '../../services/data-filters.service';
import { SystemGroupsService } from '../../services/system-groups.service';
import { CountryAndRegionSelectorComponent } from '../dropdown/country-and-region-selector/country-and-region-selector.component';
import { ExportDataMapComponent } from '../map/export-data-map/export-data-map.component';
import { WorkerService } from '../../services/worker.service';
import { CategoryService } from '../../services/categories.service';
import { ExportService } from '../../services/export.service';
import { ExportRequest } from '../../models/exported-data.model';
import { CustomStepsService } from '../../services/year-custom-steps.service';

@Component({
  selector: 'mpro-export-data',
  templateUrl: './export-data.component.html',
  styleUrls: ['./export-data.component.scss'],
})
export class ExportDataComponent implements OnInit, OnDestroy, OnChanges {
  faDownload = faDownload;
  faEmail = faEnvelope;
  faWarning = faExclamationTriangle;

  currencyView;

  public isCategoriesEnabled: boolean;

  public constants = constants;
  public getNameValuePair = DataFilters.getNameValuePair;
  public getCategoriesValue = DataFilters.getCategoriesValue;
  public customAgeGroups: AgeGroup[] = [];
  public customSpendingGroups: SpendingGroup[] = [];
  public dataFilters: DataFilters = new DataFilters();
  public sliderInput: number[] = [constants.initialYear, 2031];
  public fileFormat: string = 'XLSX';
  public sliderError?: string;
  public startYear: number;
  public endYear: number;
  public categoriesList: ToggleableNameValuePair[] = [];
  public categoryInputValue?: NameValuePair;
  public exportModal: {
    isOpen: boolean;
    isDownload: boolean;
    exportMessage: string;
  } = {
    isOpen: false,
    isDownload: false,
    exportMessage: '',
  };
  // used to postpone slider rendering until the modal is opened
  // slider ticks and values rendering depends on the parent size
  // the closed modal has 0 visible size and this breaks the slider render
  // map rendering is similar - depends on the container size
  public renderSliderAndMap: boolean = false;
  public isExporting: boolean = false;
  @Input() public isModalOpen: boolean = false;
  @Output() public isModalOpenChange: EventEmitter<boolean> =
    new EventEmitter();

  @ViewChild(ModalComponent) modal: ModalComponent;
  @ViewChild(CountryAndRegionSelectorComponent)
  selector: CountryAndRegionSelectorComponent;

  private _map: ExportDataMapComponent;
  @ViewChild(ExportDataMapComponent) set map(m: ExportDataMapComponent) {
    if (m) {
      // initially setter gets called with undefined
      this._map = m;
      this.onCountryAndRegionChange({
        selection: this.dataFilters.regionSelection,
      });
    }
  }

  private initialDataFilters: DataFilters = new DataFilters();
  private range: (number | undefined)[];
  private ngUnsubscribe: Subject<void> = new Subject();

  public selectedAgeGroups: AgeGroup[] = [];
  public selectedGenerations: string[] = [];
  public selectedCategories: string[] = [];
  public selectedSpendingGroups: SpendingGroup[] = [];
  public selectedCurrencyView: number = 0;
  public selectedGenders: string[] = ['All'];
  public allAgeGroupsLabel = allAgeGroupsLabel;
  public allSpendingGroupsLabel = allSpendingGroupsLabel;
  public customSteps: BehaviorSubject<CustomStep[]>;

  constructor(
    private customStepsService: CustomStepsService,
    public authResources: AuthorizedResourcesService,
    public systemGroupsService: SystemGroupsService,
    public authService: AuthService,
    private dataFiltersService: DataFiltersService,
    private dashboardService: DashboardStatisticsService,
    private workerService: WorkerService,
    private _categoriesList: CategoryService,
    private _exportService: ExportService
  ) {
    this.dashboardService
      .getCustomAgeGroups()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((ag) => (this.customAgeGroups = ag));

    this.dashboardService
      .getCustomSpendingGroups()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sg) => (this.customSpendingGroups = sg));

    this.workerService.isGeneratingFileSubject$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((sg) => (this.isExporting = sg));
  }

  public ngOnChanges(simpleChanges: SimpleChanges) {
    if (simpleChanges.isModalOpen && this.isModalOpen) {
      this.openModal();
    }
  }

  public ngOnInit() {
    this.customSteps = this.customStepsService.currentCustomSteps;
    this.startYear = this.authResources.startYear;
    this.endYear = this.authResources.endYear;
    this._categoriesList
      .getUserCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((categories) => {
        // Remove default value from dropdown list
        this.categoriesList = categories.filter(
          (category) => category.value !== '0'
        );
      });

    combineLatest([
      this.dataFiltersService.getDataFilters(),
      this.authService.categoriesEnabled$,
      this.authService.permissions$,
    ])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(([df, isCategoriesEnabled, permissions]) => {
        DataFilters.copy(df, this.dataFilters);
        this.selectedSpendingGroups = [];
        this.selectedAgeGroups = [];
        this.selectedCurrencyView = 0;
        this.selectedGenerations = [];
        this.selectedGenders = [];
        if (df.ageGroup.greaterThan || df.ageGroup.lessThan) {
          this.selectedAgeGroups = [
            new AgeGroup(df.ageGroup.greaterThan, df.ageGroup.lessThan),
          ];
        }
        if (df.currency) {
          this.selectedCurrencyView = df.currency;
        }
        if (df.generation) {
          this.selectedGenerations = [df.generation];
        }
        if (df.spendingGroup.greaterThan || df.spendingGroup.lessThan) {
          this.selectedSpendingGroups = [
            new SpendingGroup(
              df.spendingGroup.name,
              df.spendingGroup.greaterThan,
              df.spendingGroup.lessThan
            ),
          ];
        }
        if (df.gender) {
          this.selectedGenders = df.gender ? [df.gender] : ['All'];
        }
        const currentYear = new Date().getFullYear();
        this.sliderChanged([currentYear]);
        this.startYear =
          isCategoriesEnabled || !!df.regionSelection.cities.size
            ? constants.categoriesStartYear
            : constants.startYear;
        this.sliderInput = [this.startYear, this.endYear];
        this.isCategoriesEnabled = isCategoriesEnabled;
        this.customStepsService.getCustomSteps(
          isCategoriesEnabled || !!df.regionSelection.cities.size,
          permissions.allowedYears
        );
      });
    this.currencyView = constants.data.currencyView.map((item) => ({
      ...item,
      hidden: this.isCategoriesEnabled && item.name === '2021 PPP USD',
    }));
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onCategoryChange(category: string) {
    if (this.selectedCategories.includes(category)) {
      this.selectedCategories = this.selectedCategories.filter(
        (selectedCategory) => selectedCategory !== category
      );
    } else {
      this.selectedCategories.push(category);
    }
  }

  public onSliderError(error?: string) {
    this.sliderError = error;
  }

  private getFileNameWithTimestamp(): String {
    const today = new Date();
    const year = today.getFullYear();
    const month = `${today.getMonth() + 1}`.padStart(2, '0');
    const day = `${today.getDate()}`.padStart(2, '0');
    const stringDate = [year, month, day].join('');

    return stringDate + '_wdp_data';
  }

  public onCurrencyViewChange(currency: number) {
    this.selectedCurrencyView = currency;
  }

  public onExportData(download: boolean = false) {
    let dataRequest = new ExportRequest(
      Array.from(
        new Array(Math.abs((this.range[1] || 0) - (this.range[0] || 0)) + 1),
        (_, i) => (this.range[0] || 0) + i * 1
      ),
      this.selectedAgeGroups.map((ageGroup) => {
        return new RangeField(ageGroup.greaterThan, ageGroup.lessThan);
      }),
      this.selectedSpendingGroups.map((spendingGroup) => {
        return new RangeField(
          spendingGroup.greaterThan,
          spendingGroup.lessThan
        );
      }),
      this.dataFilters.regionSelection.world
        ? []
        : Array.from(this._map.selectedCountries),
      this.selectedGenders.length && this.selectedGenders[0] !== 'All'
        ? this.selectedGenders
        : undefined,
      ['year', 'state'],
      undefined,
      this.selectedCurrencyView ? 'REAL_USD' : 'PPP',
      // Category 0 is reserved for Total Spending
      this.isCategoriesEnabled && this.selectedCategories.length > 0
        ? this.selectedCategories
        : undefined,
      undefined,
      Array.from(this.dataFilters.regionSelection.cities).map(
        ([_, city]) => city.name
      ),
      this.selectedGenerations,
      this.dataFilters.regionSelection.world
    );
    this.workerService.setGenerateStatus(true);
    if (download) {
      return this.dashboardService
        .loadExportSaveFile(dataRequest, this.fileFormat)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (blob) => {
            this.exportModal = {
              isOpen: true,
              isDownload: download,
              exportMessage:
                'Thank you for your request, your download will start shortly',
            };
            const filename = `${this.getFileNameWithTimestamp()}.${this.fileFormat.toLowerCase()}`;
            this._exportService.saveFile(blob, filename);
            this.workerService.setGenerateStatus(false);
          },
          (err) => {
            this.workerService.setGenerateStatus(false);
            alert('Problem while exporting the file. Please try again.');
            console.error(err);
          }
        );
    }
    return this.dashboardService
      .loadExportFile(dataRequest, this.fileFormat)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(
        () => {
          this.exportModal = {
            isOpen: true,
            isDownload: download,
            exportMessage:
              'Thank you for your request, your email will be sent shortly',
          };
          this.workerService.setGenerateStatus(false);
        },
        (err) => {
          this.workerService.setGenerateStatus(false);
          alert('Problem while exporting the file. Please try again.');
          console.error(err);
        }
      );
  }

  public openModal() {
    // save the inital state to restore when modal is closed
    DataFilters.copy(this.dataFilters, this.initialDataFilters);
    // range is reset here because there is a rendering issue if it gets reset on modal close
    this.sliderInput = [constants.initialYear, 2031];
    setTimeout(() => {
      this.renderSliderAndMap = true;
    });
  }

  public onExportModalClose() {
    this.exportModal = {
      isOpen: false,
      isDownload: false,
      exportMessage: '',
    };
  }

  public onModalClose() {
    DataFilters.copy(this.initialDataFilters, this.dataFilters);
    this.sliderError = undefined;
    this.renderSliderAndMap = false;
    this.isModalOpenChange.emit(false);
    this.dataFiltersService.setDataFilters(this.initialDataFilters);
  }

  public onCountryAndRegionChange(sel: {
    selection: RegionSelection;
    dataLens?: number | undefined;
  }) {
    this.dataFilters = {
      ...this.dataFilters,
      regionSelection: sel.selection,
    };
    if (sel.selection.world) {
      this._map.selectMap();
    } else {
      this._map.selectedCountries = new Set(sel.selection.getCountryCodes());
    }
    this._map.worldMap?.changed();
    this._map.map?.changed();
  }

  public mapSelectionChanged(c: string) {
    let country = this.selector.countries.get(c);
    if (!country) {
      return;
    }

    const currentSelection: RegionSelection = this.dataFilters.regionSelection;
    currentSelection.toggleCountry(country, true);

    this.dataFilters = {
      ...this.dataFilters,
      regionSelection: currentSelection,
    };
  }

  public genderChanged(selection: string[]) {
    this.selectedGenders = selection.length ? selection : ['All'];
  }

  public ageGroupChanged(selection: AgeGroup[]) {
    this.selectedAgeGroups = selection;
  }

  public generationChanged(selection: string[]) {
    this.selectedGenerations = selection;
  }

  public spendingGroupChanged(selection: SpendingGroup[]) {
    this.selectedSpendingGroups = selection;
  }

  public fileFormatChanged(selection: NameValuePair) {
    this.fileFormat = selection.value;
  }

  public sliderChanged(range: (number | undefined)[]): void {
    if (this.range?.[0] === range?.[0] && this.range?.[1] === range?.[1]) {
      return;
    }
    this.range = range;
    this.sliderInput = [...range] as [number, number];
  }

  public ageGenerationChanged(ageGeneration: (AgeGroup | string)[]) {
    if (typeof ageGeneration?.[0] === 'string') {
      this.selectedGenerations = ageGeneration as string[];
      this.selectedAgeGroups = [];
      return;
    }
    this.selectedGenerations = [];
    this.selectedAgeGroups = ageGeneration as AgeGroup[];
  }

  public getCategoryValue() {
    // If the default value is selected, pass undefined to the dropdown so the placeholder is displayed.
    if (this.selectedCategories.length === 0) {
      return undefined;
    }
    const categoryNames = this.selectedCategories.map(
      (selectedCategory) =>
        this.getCategoriesValue(this.categoriesList, selectedCategory).name
    );
    return categoryNames.join(', ');
  }

  public isExportProhibited() {
    return (
      this.selectedSpendingGroups.length >= 6 ||
      this.dataFilters.regionSelection.cities.size >= 50 ||
      this.isExporting ||
      this.sliderError ||
      (this.isCategoriesEnabled && !this.getCategoryValue())
    );
  }
}
